import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TablePagination,
  alpha,
  useTheme,
  Stack,
  Card,
  Container,
  CircularProgress,
  Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchUpcomingSubscriptionEnd } from "../api/subscription";
import { getDateDifference } from "../utils/dateUtil";
import { pickSubscriptionLead } from "../api/subscriptionLead";
import { useUser } from "../context/UserContext";
import CommonDialog from "../components/commonDialog";
import CustomSnackbarAlert from "../components/customAlert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  CheckCircle as PickIcon,
  Timer as TimerIcon 
} from "@mui/icons-material";

export default function SubscriptionExpiry() {
  const theme = useTheme();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showdialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [selectedEntry, setSelectedEntry] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  const { user } = useUser();
  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlert] = useState(false);

  const sortedSubscriptions = subscriptionData?.subscriptions
    ? [...subscriptionData.subscriptions].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = a?.[sortConfig.key] ?? "";
        const bValue = b?.[sortConfig.key] ?? "";

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      })
    : [];

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const isSameKey = prevConfig.key === key;
      const newDirection =
        isSameKey && prevConfig.direction === "asc" ? "desc" : "asc";
      return { key, direction: newDirection };
    });
  };

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const result = await fetchUpcomingSubscriptionEnd({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (result.status) {
        setSubscriptionData(result.data);
        setPagination(result.data.pagination);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDialog = (subscriptionId) => {
    setSelectedEntry(subscriptionId);
    setDialogTitle("Pick Potential Lead");
    setDialogContent("Are you sure you want to assign this expiring subscription to yourself?");
    setShowDialog(true);
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  const handleCloseSnackbar = () => {
    setShowAlert(false);
  };

  const handleAgree = async () => {
    const data = await pickSubscriptionLead(selectedEntry, user._id);
    if (data.status) {
      loadSubscription();
      setAlertMessage("Lead picked successfully");
      setShowAlert(true);
      navigate(`/subscription-lead?id=${data.data._id}`);
    }
    setShowDialog(false);
  };

  useEffect(() => {
    loadSubscription();
  }, [page, rowsPerPage]);

  if (loading && !subscriptionData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Subscription Expiries
            </Typography>
            <Chip 
              label={`${pagination.totalCount} Upcoming`} 
              color="warning" 
              sx={{ fontWeight: 800, borderRadius: 2 }} 
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monitor and pick leads for users with nearing subscription ends
          </Typography>
        </Box>
      </Box>

      {error ? (
        <Typography color="error" textAlign="center">{error}</Typography>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { key: "name", label: "Customer" },
                    { key: "email", label: "Contact Info" },
                    { key: "subscriptionEndDate", label: "Status" },
                  ].map((column) => (
                    <TableCell
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      sx={{
                        fontWeight: 800,
                        backgroundColor: 'grey.50',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: "pointer",
                        py: 2,
                        "&:hover": { color: 'primary.main' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {column.label}
                        {sortConfig.key === column.key && (
                          sortConfig.direction === "asc" ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' }}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedSubscriptions.map((subscription, index) => {
                  const diff = getDateDifference(subscription.subscriptionEndDate);
                  const isUrgent = diff.includes("day") || diff.includes("today") || (diff.includes("days") && parseInt(diff) < 7);
                  
                  return (
                    <TableRow key={index} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{subscription.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <EmailIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography variant="caption">{subscription.email}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography variant="caption">{subscription.phone}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<TimerIcon style={{ fontSize: '0.9rem', color: 'inherit' }} />}
                          label={diff}
                          size="small"
                          variant="outlined"
                          color={isUrgent ? "error" : "primary"}
                          sx={{ fontWeight: 700, borderRadius: 1.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<PickIcon />}
                          sx={{ 
                            textTransform: "none", 
                            fontWeight: 800,
                            borderRadius: 2,
                            px: 2
                          }}
                          onClick={() => openDialog(subscription._id)}
                        >
                          Pick Lead
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
          />
        </Card>
      )}

      <CommonDialog
        open={showdialog}
        title={dialogTitle}
        content={dialogContent}
        onCancel={handleCancel}
        onAgree={handleAgree}
      />
      <CustomSnackbarAlert
        message={alertMessage}
        severity={alertSeverity}
        open={showAlert}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

